// src/utils/authInterceptor.ts
//
// One place that notices the session has ended.
//
// Every service in this app calls bare `axios` — there is no shared instance —
// so a single interceptor on the default instance covers all of them, and no
// service file has to change.
//
// Before this, nothing watched for a 401 at all: exactly one screen in the
// codebase reacted to one, and the rest let it fall through to whatever generic
// error they happened to render. Combined with an `authToken` cookie that
// outlives its JWT by six days, a signed-out user kept seeing a signed-in app
// where nothing worked. The most expensive symptom was an Oura credential
// modal displaying "Authentication required: send a session Bearer token" —
// our auth error, shown inside a form about someone else's WordPress password.

import axios from "axios";
import { endExpiredSession } from "./session";
import { VIEW_AS_REFUSED_CODE, VIEW_AS_REFUSED_EVENT } from "./viewAs";

/**
 * Requests where a 401 is the ANSWER, not a dead session.
 *
 * These are the pre-auth endpoints: the caller is signed out by definition, so
 * a 401 means "those credentials are wrong" and the form must render its own
 * message. Redirecting here would replace "incorrect password" with a silent
 * page reload — the user would never learn what they typed wrong.
 *
 * Verified against the backend rather than assumed: `/auth/login` answers
 * `401 UNAUTHORIZED` for a bad password (app/interface/routers/auth.py), which
 * is exactly the collision this list exists to prevent.
 *
 * Deliberately NARROW. The authenticated `/auth/*` routes — change-password,
 * update-hr-profile, connect/google — are absent on purpose: a 401 there really
 * does mean the session died, and they answer user error with 400, not 401, so
 * excluding them would only hide real expiries.
 */
const PRE_AUTH_PATHS = [
    "/auth/login",
    "/auth/access_token",
    "/auth/google-sso",
    "/auth/job-seeker/google",
    "/auth/register/",
];

function isPreAuthRequest(url: string | undefined): boolean {
    if (!url) return false;
    return PRE_AUTH_PATHS.some((p) => url.includes(p));
}

/**
 * Install the global 401 handler. Call once, at startup, before anything
 * renders.
 *
 * Only 401 ends the session. A **403** is deliberately left alone: "you are
 * signed in but not allowed to do this" is a permission problem, and signing
 * someone out over it would be both wrong and impossible to diagnose.
 */
export function installAuthInterceptor(): void {
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const url: string | undefined = error?.config?.url;
            if (status === 401 && !isPreAuthRequest(url)) {
                endExpiredSession();
            }
            // Staff viewing as the client cannot write (ourteam-backend#5712).
            // Callers show their own generic failure for a refused write, so
            // the sentence the API returned is surfaced here once, for all of
            // them, by whatever is listening (the merchant shell's notice).
            if (status === 403 && error?.response?.data?.data?.code === VIEW_AS_REFUSED_CODE) {
                const sentence = error.response.data.message || error.response.data.detail;
                window.dispatchEvent(new CustomEvent(VIEW_AS_REFUSED_EVENT, { detail: typeof sentence === "string" ? sentence : "" }));
            }
            // Always rethrow. The redirect is a side effect, not a substitute
            // for the caller's own error handling: navigation is asynchronous,
            // so the calling code still runs afterwards and must not be handed
            // a silently-resolved promise it will read as success.
            return Promise.reject(error);
        },
    );
}
